import { Db } from 'mongodb'
import { schema, types } from 'papr'

const tocItemType = types.object({
	id: types.string({ required: true }),
	level: types.number({ required: true }),
	parentId: types.string(),
	previousLevel: types.number(),
	slug: types.string({ required: true }),
	title: types.string({ required: true }),
})

export const postSchema = schema(
	{
		title: types.string({ required: true }),
		slug: types.string({ required: true }),
		author: types.objectId({ required: true }),
		coAuthors: types.array(types.objectId()),
		// TODO: to be retired
		cuid: types.string({ required: true }),
		// TODO: to be retired
		type: types.enum(['story' as const]),
		// TODO: to be retired
		partOfPublication: types.boolean(),
		dateAdded: types.date({ required: true }),
		/**
		 * All-time views.
		 *
		 * Not realtime but updated periodically based on Timestream data.
		 */
		views: types.number(),
		tags: types.array(types.objectId({ required: true }), { required: true }),
		isActive: types.boolean(),
		publication: types.objectId({ required: true }),
		coverImage: types.string(),
		contentMarkdown: types.string({ required: true }),
		content: types.string({ required: true }),
		/**
		 * Is this post an AMA (Ask Me Anything) post?
		 * We still have support existing AMA posts, but we don't allow new AMA posts.
		 */
		isAMA: types.boolean(),
		series: types.objectId(),
		hasCustomDate: types.boolean(),
		sourcedFromGithub: types.boolean(),
		hasLatex: types.boolean(),
		popularity: types.number(),
		discussionScore: types.number(),
		// Used for calculating discussion score
		commentMetadata: types.object({
			qualityCommentCharLength: types.number(),
			qualityCommentCount: types.number(),
			totalCommentCount: types.number(),
			lastCommentAddedDate: types.date(),
		}),
		enableToc: types.boolean(),
		isCoverAttributionHidden: types.boolean(),
		coverImageAttribution: types.string(),
		coverImagePhotographer: types.string(),
		// for some reason the `toc` sometimes is an array of arrays or an array of objects
		toc: types.array(types.oneOf([tocItemType, types.array(tocItemType)])),
		isCoverImagePortrait: types.boolean(),
		// TODO: this should be changed to "shadowBanned"
		sB: types.boolean(),
		noIndex: types.boolean(), // set to true when a post is shadowbanned by our bot
		isRepublished: types.boolean(),
		autoPublishedFromRSS: types.boolean(),
		readTime: types.number(),
		// TODO: we need to change this into having strict types.
		githubAsSourceMeta: types.objectGeneric(types.any()),
		draft: types.objectId(),
		subtitle: types.string(),
		url: types.string(),
		audioUrls: types.object({
			male: types.string(),
			female: types.string(),
		}),
		autoGeneratedCover: types.string(),
		stickCoverToBottom: types.boolean(),
		fallBackCoverImage: types.string(),
		brief: types.string({ required: true }),
		originalArticleURL: types.string(),
		ogImage: types.string(),
		metaTitle: types.string(),
		metaDescription: types.string(),
		untaggedFrom: types.array(types.objectId()),
		majorNode: types.objectId(),
		dateUpdated: types.date(),
		lastActivity: types.date(),
		host: types.string(),
		badges: types.array(
			types.object({
				badgeId: types.objectId({ required: true }),
				name: types.string({ required: true }),
			}),
		),
		hideBadges: types.boolean(),
		responseCount: types.number(),
		replyCount: types.number(),
		bestResponse: types.string(),
		isFeatured: types.boolean(),
		isEngaging: types.boolean(),
		dateFeatured: types.date(),
		contributors: types.array(
			types.object({
				user: types.objectId(),
				stamp: types.string(),
			}),
		),
		isDelisted: types.boolean(),
		isNotified: types.boolean(),
		duplicatePosts: types.array(types.objectId()),
		newDiscussion: types.boolean(),
		numCollapsed: types.number(),
		metaData: types.object({
			github: types.object({
				stars: types.number(),
				forks: types.number(),
				watchers: types.number(),
				owner: types.string(),
				repo: types.string(),
				lastRefreshed: types.date(),
			}),
		}),
		isOriginal: types.boolean(),
		similarPostIds: types.array(types.objectId()),
		isSystemPost: types.boolean(),
		altSystemHeading: types.string(),
		altSystemDesc: types.string(),
		isIntroduction: types.boolean(),
		reactions: types.array(types.objectId()),
		// TODO: this needs to be implemented properly. It uses virtuals in Hashnode-Next.
		reactionToCountMap: types.any(),
		totalReactions: types.number(),
		totalReactionsByCurrentUser: types.number(),
		reactionsByCurrentUser: types.array(types.objectId()),
		nonPublication: types.boolean(),
		importedFromMedium: types.boolean(),
		hasPinnedTag: types.boolean(),
		isPinnedToBlog: types.boolean(),
		disableComments: types.boolean(),
		commentsPaused: types.boolean(),
		syncAlgolia: types.boolean(),
		// TODO: This is supposed to be calculated on demand in your functions
		numUniqueUsersWhoReacted: types.number(),
		hasScheduledDate: types.boolean(),
		arweave: types.object({
			id: types.string(),
			contentDigest: types.string(),
			ethAddress: types.string(),
		}),
		dateDeleted: types.date(),
		slugOverridden: types.boolean(),
		isNewsletterActivated: types.boolean(),
		tweetOptions: types.object({
			enabled: types.boolean(),
			text: types.string(),
		}),
	},
	{
		timestamps: true,
		defaults: {
			views: 0,
			isActive: true,
			sourcedFromGithub: false,
			hasLatex: false,
			popularity: 0,
			discussionScore: 0,
			enableToc: false,
			type: 'story' as const,
			partOfPublication: true,
			responseCount: 0,
			replyCount: 0,
			isFeatured: false,
			isEngaging: false,
			isDelisted: false,
			isNotified: false,
			numCollapsed: 0,
			reactions: [],
			totalReactions: 0,
			totalReactionsByCurrentUser: 0,
			isPinnedToBlog: false,
			disableComments: false,
			commentsPaused: false,
			syncAlgolia: false,
			numUniqueUsersWhoReacted: 0,
			slugOverridden: false,
			tweetOptions: {
				enabled: false,
			},
		},
	},
)

export const createPostsIndex = async (db: Db) => {
	await db.createIndex('posts', {
		isActive: 1,
		partOfPublication: 1,
		publication: 1,
		type: 1,
		dateAdded: -1,
		_id: -1,
		tags: 1,
	})

	await db.createIndex('posts', {
		partOfPublication: 1,
		type: 1,
		popularity: -1,
		tags: 1,
		isActive: 1,
		isDelisted: 1,
		sB: 1,
	})

	await db.createIndex('posts', {
		partOfPublication: 1,
		type: 1,
		popularity: -1,
		publication: 1,
		isActive: 1,
		isDelisted: 1,
		sB: 1,
	})

	await db.createIndex('posts', {
		isActive: 1,
		type: 1,
		popularity: -1,
		isDelisted: 1,
		sB: 1,
		totalReactions: 1,
	})
}

export type PostDocument = (typeof postSchema)[0]
export type PostSchemaOptions = (typeof postSchema)[1]
